import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Printer } from '../../lib/icons';
import { getBranding } from '../../lib/branding';
import { EmissionFacture } from './EmissionFacture';
import { QrLien } from './QrLien';

const portalNode = () => {
  let el = document.getElementById('print-portal');
  if (!el) {
    el = document.createElement('div');
    el.id = 'print-portal';
    document.body.appendChild(el);
  }
  return el;
};

// Les bons et reçus s'impriment en A5, les factures en A4.
const formatAuto = (type = '') =>
  /facture/i.test(type) ? 'a4' : /^(bon|reçu|recu|ticket)/i.test(type.trim()) ? 'a5' : 'a4';

// Gabarit unique de tous les documents imprimables (bons, factures, tickets, reçus).
// `exemplaires` : libellés des copies. Chaque exemplaire occupe sa PROPRE page A5
// (saut de page explicite, jamais deux exemplaires empilés sur une A4).
// `autoPrint` : lance l'impression une seule fois, après le rendu réel du document
// et le chargement de ses ressources (logo, QR).
export const PrintDoc = ({ type, numero, date, tiers, lignes, totaux, mentions, qrUrl, qrLegende, signatures, format, compact = false, enfants, emission, exemplaires, autoPrint, onClose }) => {
  const b = getBranding();
  const [node] = useState(portalNode);
  const fmt = format ?? formatAuto(type);
  const a5 = fmt === 'a5';
  const tousExemplaires = exemplaires?.length > 1 ? exemplaires : null;
  // Réimpression manuelle : « tous » ou un seul exemplaire, identifié par son libellé.
  const [choix, setChoix] = useState('tous');
  const copies = tousExemplaires
    ? choix === 'tous'
      ? tousExemplaires
      : [tousExemplaires[Number(choix)]]
    : null;
  const lance = useRef(false);

  useEffect(() => {
    const style = document.createElement('style');
    style.id = 'print-page-format';
    // Les bons / tickets restent en A5 même en deux exemplaires : une page A5 chacun.
    style.textContent = a5
      ? '@media print{@page{size:A5 portrait;margin:7mm}}'
      : '@media print{@page{size:A4 portrait;margin:12mm}}';
    document.head.appendChild(style);
    return () => style.remove();
  }, [a5]);

  useEffect(() => {
    if (!autoPrint || lance.current) return;
    lance.current = true;
    let annule = false;
    const trame = () => new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
    (async () => {
      await trame();
      if (document.fonts?.ready) await document.fonts.ready;
      // QR généré de façon asynchrone : on attend son image avant d'imprimer.
      if (qrUrl) {
        for (let essai = 0; essai < 40 && !node.querySelector('[data-testid="qr-lien"] img'); essai += 1)
          await new Promise((r) => setTimeout(r, 50));
      }
      await Promise.all(
        Array.from(node.querySelectorAll('img')).map((img) =>
          img.complete && img.naturalWidth > 0
            ? Promise.resolve()
            : new Promise((r) => {
                img.addEventListener('load', r, { once: true });
                img.addEventListener('error', r, { once: true });
              })
        )
      );
      await trame();
      if (!annule) window.print();
    })();
    return () => {
      annule = true;
    };
  }, [autoPrint, node, qrUrl]);

  const doc = (
    <div
      data-print-format={fmt}
      data-print-compact={compact || undefined}
      className={`mx-auto bg-white text-slate-900 ${a5 ? 'max-w-[520px] p-5 doc-a5' : 'max-w-[780px] p-8'}`}
    >
      <header className="flex items-start justify-between gap-6 border-b-2 border-slate-900 pb-4">
        <div className="flex items-start gap-3">
          {b.logo && <img src={b.logo} alt="Logo" className={`object-contain ${a5 ? 'h-12 w-12' : 'h-20 w-20'}`} />}
          <div>
            <p className="font-heading text-lg font-black tracking-tight">{b.nom}</p>
            <p className="font-data text-[11px] text-slate-600">{b.slogan}</p>
            <p className="mt-1 font-data text-[11px] text-slate-600">{b.adresse}</p>
            <p className="font-data text-[11px] text-slate-600">
              Tél. {b.tel}
              {b.tel2 ? ` / ${b.tel2}` : ''} · {b.email}
            </p>
          </div>
        </div>
        <div className="flex items-start gap-4">
          <div className="text-right">
            <p className={`font-heading font-bold uppercase tracking-[0.06em] ${a5 ? 'text-sm' : 'text-base'}`}>{type}</p>
            <p className="font-data text-xs text-slate-700">N° {numero}</p>
            <p className="font-data text-xs text-slate-700">Date : {date}</p>
            <p className="mt-2 border border-slate-400 px-2 py-1 font-data text-[10px] uppercase tracking-[0.08em]">
              Matricule fiscal : {b.mf || '________________'}
            </p>
          </div>
          {qrUrl && <QrLien url={qrUrl} legende={qrLegende ?? 'Scannez pour suivre en ligne'} />}
        </div>
      </header>

      {tiers && (
        <section className="mt-4 border border-slate-300 px-3 py-2">
          <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-slate-500">Tiers concerné</p>
          <p className="font-heading text-sm font-bold">{tiers}</p>
        </section>
      )}

      {enfants}

      <table className="mt-5 w-full border-collapse text-left">
        <tbody>
          {(lignes ?? []).map(([k, v]) => (
            <tr key={k} className="border-b border-slate-200">
              <th className="w-1/2 px-2 py-1.5 text-[11px] font-bold uppercase tracking-[0.08em] text-slate-500">
                {k}
              </th>
              <td className="px-2 py-1.5 font-data text-sm font-semibold">{v}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {totaux?.length > 0 && (
        <dl className="ml-auto mt-4 w-full max-w-xs space-y-1 font-data text-sm">
          {totaux.map(([k, v], i) => (
            <div
              key={k}
              className={`flex justify-between ${i === totaux.length - 1 ? 'border-t-2 border-slate-900 pt-1 text-base font-bold' : ''}`}
            >
              <dt className={i === totaux.length - 1 ? 'uppercase tracking-[0.06em]' : 'text-slate-500'}>{k}</dt>
              <dd className="tabular font-semibold">{v}</dd>
            </div>
          ))}
        </dl>
      )}

      {mentions && <p className="mt-5 font-data text-[11px] text-slate-600">{mentions}</p>}

      <footer className={`grid grid-cols-2 border-t border-slate-300 ${a5 ? 'mt-5 gap-6 pt-3' : 'mt-10 gap-10 pt-6'}`}>
        {(signatures ?? ['Signature & cachet — Huilerie', 'Signature — Tiers']).map((s) => (
          <div key={s}>
            <p className="text-[10px] font-bold uppercase tracking-[0.1em] text-slate-500">{s}</p>
            <div className={`border-t border-slate-400 ${a5 ? 'mt-6' : 'mt-10'}`} />
          </div>
        ))}
      </footer>
      <p className="mt-4 text-center font-data text-[9px] uppercase tracking-[0.12em] text-slate-400">
        {b.nom} · MF {b.mf} · Document généré par Huilerie OS
      </p>
    </div>
  );

  const imprimable = copies ? (
    <div data-testid="print-doc-exemplaires">
      {copies.map((libelle, i) => (
        <div
          key={libelle}
          data-testid={`print-doc-page-${i + 1}`}
          className={i > 0 ? 'mt-3 border-t-2 border-dashed border-slate-400 pt-3 print:mt-0 print:border-0 print:pt-0' : ''}
          style={
            i < copies.length - 1
              ? { breakAfter: 'page', pageBreakAfter: 'always' }
              : undefined
          }
        >
          <p
            data-testid={`print-doc-exemplaire-${i + 1}`}
            className="mb-1 text-center font-data text-[9px] font-bold uppercase tracking-[0.18em] text-slate-500"
          >
            {libelle}
          </p>
          {doc}
        </div>
      ))}
    </div>
  ) : (
    doc
  );

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-slate-900/60 p-4" data-testid="print-doc">
        <div className="w-full max-w-3xl border-2 border-slate-900 bg-white">
          <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
            <p className="font-heading text-base font-bold text-slate-900">{type} — aperçu <span className="ml-2 border border-slate-300 px-1.5 py-0.5 font-data text-[10px] uppercase text-slate-500" data-testid="print-doc-format">{a5 ? `Format A5${copies ? ` · ${copies.length} exemplaires` : ''}` : `Format A4${copies ? ` · ${copies.length} exemplaires` : ''}`}</span></p>
            <div className="flex gap-2">
              {tousExemplaires && (
                <select
                  data-testid="print-doc-choix-exemplaire"
                  value={choix}
                  onChange={(e) => setChoix(e.target.value)}
                  className="h-10 border border-slate-300 bg-white px-2 font-data text-xs text-slate-800 outline-none focus:border-slate-900"
                >
                  <option value="tous">Les {tousExemplaires.length} exemplaires — {tousExemplaires.length} exemplaires {a5 ? 'A5' : 'A4'}</option>
                  {tousExemplaires.map((libelle, i) => (
                    <option key={libelle} value={String(i)}>
                      {libelle} seul — {a5 ? 'A5' : 'A4'}
                    </option>
                  ))}
                </select>
              )}
              <button
                type="button"
                data-testid="print-doc-imprimer"
                onClick={() => window.print()}
                className="flex h-10 items-center gap-2 bg-slate-900 px-4 font-heading text-sm font-bold text-white transition-colors duration-150 hover:bg-slate-700"
              >
                <Printer size={15} /> Imprimer / PDF
              </button>
              <button
                type="button"
                data-testid="print-doc-fermer"
                onClick={onClose}
                className="border border-slate-300 p-2 text-slate-600 transition-colors duration-150 hover:bg-slate-900 hover:text-white"
              >
                <X size={16} />
              </button>
            </div>
          </div>
          <div className="max-h-[70vh] overflow-y-auto bg-slate-100 p-4">
            {emission && (
              <div className="mb-3 print:hidden" data-testid="print-doc-emission">
                <EmissionFacture operation={emission} onEmis={emission.onEmis} />
              </div>
            )}
            <div className="border border-slate-300 shadow-sm">{imprimable}</div>
          </div>
        </div>
      </div>
      {createPortal(imprimable, node)}
    </>
  );
};
